import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useI18n } from '../../i18n/I18nContext';
import api from '../../utils/api';

function AdminOffers() {
  const { user } = useAuth();
  const { t } = useI18n();

  const [offers, setOffers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    vendor_id: '', category_id: '', title: '', short_description: '', fine_print: '',
    lat: '', lng: '', website_url: '', whatsapp_number: '', line_url: '', phone_number: '', google_maps_url: '', active: true
  });

  // Image upload state
  const [uploadingImage, setUploadingImage] = useState(false);
  const [currentOfferImage, setCurrentOfferImage] = useState(null);
  const fileInputRef = useRef(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterVendor, setFilterVendor] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('title');

  useEffect(function() {
    if (user) loadData();
  }, [user]);

  function loadData() {
    Promise.all([
      api.get('/admin/offers'),
      api.get('/admin/vendors'),
      api.get('/admin/categories')
    ]).then(function(results) {
      setOffers(results[0]);
      setVendors(results[1]);
      setCategories(results[2]);
      setLoading(false);
    }).catch(function(err) {
      console.error('Failed to load:', err);
      setLoading(false);
    });
  }

  function handleSubmit(e) {
    e.preventDefault();
    var data = {
      vendor_id: form.vendor_id,
      category_id: form.category_id,
      title: form.title,
      short_description: form.short_description,
      fine_print: form.fine_print,
      lat: form.lat ? parseFloat(form.lat) : null,
      lng: form.lng ? parseFloat(form.lng) : null,
      website_url: form.website_url,
      whatsapp_number: form.whatsapp_number,
      line_url: form.line_url,
      phone_number: form.phone_number,
      google_maps_url: form.google_maps_url,
      active: form.active
    };

    var promise = editingId
      ? api.put('/admin/offers/' + editingId, data)
      : api.post('/admin/offers', data);

    promise.then(function() {
      setShowForm(false);
      setEditingId(null);
      resetForm();
      loadData();
    }).catch(function(err) {
      alert('Error: ' + err.message);
    });
  }

  function resetForm() {
    setForm({
      vendor_id: '', category_id: '', title: '', short_description: '', fine_print: '',
      lat: '', lng: '', website_url: '', whatsapp_number: '', line_url: '', phone_number: '', google_maps_url: '', active: true
    });
    setCurrentOfferImage(null);
  }

  function handleEdit(offer) {
    setForm({
      vendor_id: offer.vendor_id,
      category_id: offer.category_id,
      title: offer.title,
      short_description: offer.short_description || '',
      fine_print: offer.fine_print || '',
      lat: offer.lat || '',
      lng: offer.lng || '',
      website_url: offer.website_url || '',
      whatsapp_number: offer.whatsapp_number || '',
      line_url: offer.line_url || '',
      phone_number: offer.phone_number || '',
      google_maps_url: offer.google_maps_url || '',
      active: offer.active
    });
    setCurrentOfferImage(offer.image_url);
    setEditingId(offer.id);
    setShowForm(true);
  }

  function handleDelete(id) {
    if (!window.confirm(t('admin.deleteOffer'))) return;
    api.delete('/admin/offers/' + id).then(function() {
      loadData();
    }).catch(function(err) {
      alert('Error: ' + err.message);
    });
  }

  // Toggle global/local default status
  function handleToggleDefault(offerId, field, currentValue) {
    var updateData = {};
    updateData[field] = !currentValue;
    
    api.put('/admin/offers/' + offerId + '/default-status', updateData)
      .then(function() {
        // Update local state
        setOffers(function(prev) {
          return prev.map(function(o) {
            if (o.id === offerId) {
              var updated = Object.assign({}, o);
              updated[field] = !currentValue;
              return updated;
            }
            return o;
          });
        });
      })
      .catch(function(err) {
        alert('Error updating default status: ' + err.message);
      });
  }


  function handleImageUpload(e) {
    var file = e.target.files[0];
    if (!file) return;

    // Validate file type
    var validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (validTypes.indexOf(file.type) === -1) {
      alert('Please upload a JPEG, PNG, or WebP image.');
      return;
    }

    // Validate file size (1MB max)
    if (file.size > 1024 * 1024) {
      alert('Image must be less than 1MB.');
      return;
    }

    setUploadingImage(true);

    // Upload to server
    api.upload('/upload', file).then(function(result) {
      // Update offer with new image URL
      return api.post('/admin/offers/' + editingId + '/image', { image_url: result.url });
    }).then(function() {
      // Reload data to get updated image URL
      return api.get('/admin/offers');
    }).then(function(offersData) {
      setOffers(offersData);
      var updated = offersData.find(function(o) { return o.id === editingId; });
      if (updated) {
        setCurrentOfferImage(updated.image_url);
      }
      setUploadingImage(false);
    }).catch(function(err) {
      alert('Upload failed: ' + err.message);
      setUploadingImage(false);
    });

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }

  function handleRemoveImage() {
    if (!window.confirm('Remove this image?')) return;

    api.delete('/admin/offers/' + editingId + '/image').then(function() {
      setCurrentOfferImage(null);
      loadData();
    }).catch(function(err) {
      alert('Error: ' + err.message);
    });
  }

  function getOfferImage(offer) {
    if (offer.image_url) return offer.image_url;
    var cat = categories.find(function(c) { return c.id === offer.category_id; });
    if (cat && cat.default_image_url) return cat.default_image_url;
    return '/img/defaults/generic.svg';
  }

  // Filter and sort offers
  var filteredOffers = offers
    .filter(function(o) {
      var matchesSearch = !searchTerm ||
        o.title.toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1 ||
        (o.vendor_name && o.vendor_name.toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1);
      var matchesVendor = !filterVendor || o.vendor_id === filterVendor;
      var matchesCategory = !filterCategory || o.category_id === filterCategory;
      var matchesStatus = filterStatus === 'all' ||
        (filterStatus === 'active' && o.active) ||
        (filterStatus === 'inactive' && !o.active);
      return matchesSearch && matchesVendor && matchesCategory && matchesStatus;
    })
    .sort(function(a, b) {
      if (sortBy === 'title') return a.title.localeCompare(b.title);
      if (sortBy === 'vendor') return (a.vendor_name || '').localeCompare(b.vendor_name || '');
      if (sortBy === 'category') return (a.category_name || '').localeCompare(b.category_name || '');
      return 0;
    });

  if (loading) {
    return React.createElement('div', { className: 'flex justify-center', style: { padding: '40px 0' } },
      React.createElement('div', { className: 'spinner' })
    );
  }

  return React.createElement('div', null,
    // Page Header
    React.createElement('div', { className: 'flex justify-between items-center mb-4' },
      React.createElement('h1', { style: { fontSize: '20px', fontWeight: '600' } }, t('admin.offers')),
      React.createElement('button', {
        onClick: function() { setShowForm(true); setEditingId(null); resetForm(); },
        className: 'btn btn-primary',
        style: { padding: '8px 16px', fontSize: '14px' }
      }, '+ ', t('common.new'))
    ),

    // Filters
    React.createElement('div', { className: 'card mb-4' },
      React.createElement('div', { className: 'flex gap-2 flex-wrap items-end' },
        React.createElement('div', { style: { flex: '2', minWidth: '200px' } },
          React.createElement('label', { className: 'label text-xs' }, t('common.search')),
          React.createElement('input', {
            type: 'text',
            className: 'input',
            placeholder: t('admin.searchByTitleVendor'),
            value: searchTerm,
            onChange: function(e) { setSearchTerm(e.target.value); }
          })
        ),
        React.createElement('div', { style: { flex: '1', minWidth: '150px' } },
          React.createElement('label', { className: 'label text-xs' }, t('admin.vendors')),
          React.createElement('select', {
            className: 'input',
            value: filterVendor,
            onChange: function(e) { setFilterVendor(e.target.value); }
          },
            React.createElement('option', { value: '' }, t('admin.allVendors')),
            vendors.map(function(v) {
              return React.createElement('option', { key: v.id, value: v.id }, v.name);
            })
          )
        ),
        React.createElement('div', { style: { flex: '1', minWidth: '150px' } },
          React.createElement('label', { className: 'label text-xs' }, t('admin.category')),
          React.createElement('select', {
            className: 'input',
            value: filterCategory,
            onChange: function(e) { setFilterCategory(e.target.value); }
          },
            React.createElement('option', { value: '' }, t('admin.allCategories')),
            categories.map(function(c) {
              return React.createElement('option', { key: c.id, value: c.id }, c.name);
            })
          )
        ),
        React.createElement('div', { style: { flex: '1', minWidth: '100px' } },
          React.createElement('label', { className: 'label text-xs' }, t('common.status')),
          React.createElement('select', {
            className: 'input',
            value: filterStatus,
            onChange: function(e) { setFilterStatus(e.target.value); }
          },
            React.createElement('option', { value: 'all' }, t('common.all')),
            React.createElement('option', { value: 'active' }, t('common.active')),
            React.createElement('option', { value: 'inactive' }, t('common.inactive'))
          )
        ),
        React.createElement('div', { style: { flex: '1', minWidth: '120px' } },
          React.createElement('label', { className: 'label text-xs' }, t('common.sortBy')),
          React.createElement('select', {
            className: 'input',
            value: sortBy,
            onChange: function(e) { setSortBy(e.target.value); }
          },
            React.createElement('option', { value: 'title' }, t('admin.titleAZ')),
            React.createElement('option', { value: 'vendor' }, t('admin.vendors')),
            React.createElement('option', { value: 'category' }, t('admin.category'))
          )
        )
      ),
      React.createElement('div', { className: 'text-secondary text-xs mt-2' },
        t('common.showing', { count: filteredOffers.length, total: offers.length }), ' ', t('admin.offers').toLowerCase()
      )
    ),

    // Form
    showForm && React.createElement('div', { className: 'card mb-4' },
      React.createElement('h3', { style: { fontSize: '16px', marginBottom: '16px' } },
        editingId ? t('admin.editOffer') : t('admin.newOffer')
      ),
      React.createElement('form', { onSubmit: handleSubmit },
        // Image upload section (only when editing)
        editingId && React.createElement('div', { className: 'form-group' },
          React.createElement('label', { className: 'label' }, 'Offer Image'),
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '16px' } },
            React.createElement('div', {
              style: {
                width: '80px',
                height: '80px',
                borderRadius: '8px',
                overflow: 'hidden',
                background: 'var(--background)',
                border: '1px solid var(--border)'
              }
            },
              React.createElement('img', {
                src: currentOfferImage || '/img/defaults/generic.svg',
                alt: 'Offer image',
                style: { width: '100%', height: '100%', objectFit: 'cover' }
              })
            ),
            React.createElement('div', null,
              React.createElement('input', {
                type: 'file',
                ref: fileInputRef,
                accept: 'image/jpeg,image/png,image/webp',
                onChange: handleImageUpload,
                style: { display: 'none' }
              }),
              React.createElement('button', {
                type: 'button',
                className: 'btn btn-secondary',
                style: { padding: '6px 12px', fontSize: '13px', marginRight: '8px' },
                disabled: uploadingImage,
                onClick: function() { fileInputRef.current && fileInputRef.current.click(); }
              }, uploadingImage ? 'Uploading...' : 'Upload Image'),
              currentOfferImage && React.createElement('button', {
                type: 'button',
                className: 'btn btn-secondary',
                style: { padding: '6px 12px', fontSize: '13px', color: 'var(--error)' },
                onClick: handleRemoveImage
              }, 'Remove')
            )
          ),
          React.createElement('p', { className: 'text-secondary text-xs mt-1' },
            'Upload a square image (recommended). Max 1MB. JPEG, PNG, or WebP.'
          )
        ),

        React.createElement('div', { className: 'form-group' },
          React.createElement('label', { className: 'label' }, t('admin.vendors'), ' *'),
          React.createElement('select', {
            className: 'input',
            value: form.vendor_id,
            onChange: function(e) { setForm(function(f) { return Object.assign({}, f, { vendor_id: e.target.value }); }); },
            required: true
          },
            React.createElement('option', { value: '' }, t('admin.selectVendor')),
            vendors.map(function(v) {
              return React.createElement('option', { key: v.id, value: v.id }, v.name);
            })
          )
        ),
        React.createElement('div', { className: 'form-group' },
          React.createElement('label', { className: 'label' }, t('admin.category'), ' *'),
          React.createElement('select', {
            className: 'input',
            value: form.category_id,
            onChange: function(e) { setForm(function(f) { return Object.assign({}, f, { category_id: e.target.value }); }); },
            required: true
          },
            React.createElement('option', { value: '' }, t('admin.selectCategory')),
            categories.map(function(c) {
              return React.createElement('option', { key: c.id, value: c.id }, c.name);
            })
          )
        ),
        React.createElement('div', { className: 'form-group' },
          React.createElement('label', { className: 'label' }, t('admin.offerTitle'), ' *'),
          React.createElement('input', {
            type: 'text',
            className: 'input',
            value: form.title,
            onChange: function(e) { setForm(function(f) { return Object.assign({}, f, { title: e.target.value }); }); },
            required: true
          })
        ),
        React.createElement('div', { className: 'form-group' },
          React.createElement('label', { className: 'label' }, t('admin.shortDescription')),
          React.createElement('textarea', {
            className: 'input',
            rows: 2,
            value: form.short_description,
            onChange: function(e) { setForm(function(f) { return Object.assign({}, f, { short_description: e.target.value }); }); }
          })
        ),
        React.createElement('div', { className: 'flex gap-2' },
          React.createElement('div', { className: 'form-group', style: { flex: 1 } },
            React.createElement('label', { className: 'label' }, t('admin.latitude')),
            React.createElement('input', {
              type: 'number',
              step: 'any',
              className: 'input',
              value: form.lat,
              onChange: function(e) { setForm(function(f) { return Object.assign({}, f, { lat: e.target.value }); }); }
            })
          ),
          React.createElement('div', { className: 'form-group', style: { flex: 1 } },
            React.createElement('label', { className: 'label' }, t('admin.longitude')),
            React.createElement('input', {
              type: 'number',
              step: 'any',
              className: 'input',
              value: form.lng,
              onChange: function(e) { setForm(function(f) { return Object.assign({}, f, { lng: e.target.value }); }); }
            })
          )
        ),
        React.createElement('div', { className: 'form-group' },
          React.createElement('label', { className: 'label' }, t('admin.websiteUrl')),
          React.createElement('input', {
            type: 'url',
            className: 'input',
            value: form.website_url,
            onChange: function(e) { setForm(function(f) { return Object.assign({}, f, { website_url: e.target.value }); }); }
          })
        ),
        React.createElement('div', { className: 'form-group' },
          React.createElement('label', { className: 'label' }, t('admin.whatsappNumber')),
          React.createElement('input', {
            type: 'text',
            className: 'input',
            value: form.whatsapp_number,
            onChange: function(e) { setForm(function(f) { return Object.assign({}, f, { whatsapp_number: e.target.value }); }); },
            placeholder: '+66812345678'
          })
        ),
        React.createElement('div', { className: 'form-group' },
          React.createElement('label', { className: 'label' }, t('common.phone')),
          React.createElement('input', {
            type: 'text',
            className: 'input',
            value: form.phone_number,
            onChange: function(e) { setForm(function(f) { return Object.assign({}, f, { phone_number: e.target.value }); }); }
          })
        ),
        React.createElement('div', { className: 'form-group' },
          React.createElement('label', { className: 'label' }, t('admin.googleMapsUrl')),
          React.createElement('input', {
            type: 'url',
            className: 'input',
            value: form.google_maps_url,
            onChange: function(e) { setForm(function(f) { return Object.assign({}, f, { google_maps_url: e.target.value }); }); }
          })
        ),
        React.createElement('div', { className: 'form-group' },
          React.createElement('label', null,
            React.createElement('input', {
              type: 'checkbox',
              checked: form.active,
              onChange: function(e) { setForm(function(f) { return Object.assign({}, f, { active: e.target.checked }); }); }
            }),
            ' ', t('common.active')
          )
        ),
        React.createElement('div', { className: 'flex gap-2' },
          React.createElement('button', { type: 'submit', className: 'btn btn-primary' }, t('common.save')),
          React.createElement('button', {
            type: 'button',
            className: 'btn btn-secondary',
            onClick: function() { setShowForm(false); }
          }, t('common.cancel'))
        )
      )
    ),

    // Offers list
    filteredOffers.length === 0
      ? React.createElement('div', { className: 'card text-center text-secondary' }, t('admin.noOffersFound'))
      : filteredOffers.map(function(o) {
          return React.createElement('div', {
            key: o.id,
            className: 'card',
            style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }
          },
            // Thumbnail
            React.createElement('div', {
              style: {
                width: '48px',
                height: '48px',
                borderRadius: '6px',
                overflow: 'hidden',
                background: 'var(--background)',
                flexShrink: 0
              }
            },
              React.createElement('img', {
                src: getOfferImage(o),
                alt: '',
                style: { width: '100%', height: '100%', objectFit: 'cover' }
              })
            ),
            // Details
            React.createElement('div', { style: { flex: 1, minWidth: 0 } },
              React.createElement('div', { className: 'flex items-center gap-2' },
                React.createElement('h3', { style: { fontSize: '14px' } }, o.title),
                !o.active && React.createElement('span', {
                  className: 'badge',
                  style: { background: 'var(--error)', color: 'white', fontSize: '10px', padding: '2px 6px' }
                }, t('common.inactive')),
                o.image_url && React.createElement('span', {
                  style: { fontSize: '10px', color: 'var(--success)' }
                }, '📷')
,
                o.is_global_default && React.createElement('span', {
                  className: 'badge',
                  style: { background: '#ffc107', color: '#000', fontSize: '10px', padding: '2px 6px', cursor: 'pointer' },
                  onClick: function() { handleToggleDefault(o.id, 'is_global_default', o.is_global_default); },
                  title: 'Click to remove global default'
                }, '⭐ Global'),
                o.is_local_default && React.createElement('span', {
                  className: 'badge',
                  style: { background: '#0d6efd', color: 'white', fontSize: '10px', padding: '2px 6px', cursor: 'pointer' },
                  onClick: function() { handleToggleDefault(o.id, 'is_local_default', o.is_local_default); },
                  title: 'Click to remove local default'
                }, '📍 Local')
              ),
              React.createElement('p', { className: 'text-secondary text-xs' }, o.vendor_name, ' • ', o.category_name)
            ),
            // Actions
            React.createElement('div', { className: 'flex gap-2' },
              React.createElement('button', {
                onClick: function() { handleToggleDefault(o.id, 'is_global_default', o.is_global_default); },
                className: 'btn btn-secondary',
                style: { padding: '4px 6px', fontSize: '10px', background: o.is_global_default ? '#ffc107' : 'transparent', color: o.is_global_default ? '#000' : 'inherit', border: '1px solid #ffc107' },
                title: o.is_global_default ? 'Remove from global defaults' : 'Make global default'
              }, '⭐'),
              React.createElement('button', {
                onClick: function() { handleToggleDefault(o.id, 'is_local_default', o.is_local_default); },
                className: 'btn btn-secondary',
                style: { padding: '4px 6px', fontSize: '10px', background: o.is_local_default ? '#0d6efd' : 'transparent', color: o.is_local_default ? 'white' : 'inherit', border: '1px solid #0d6efd' },
                title: o.is_local_default ? 'Remove from local defaults' : 'Make local default'
              }, '📍'),
              React.createElement('button', {
                onClick: function() { handleEdit(o); },
                className: 'btn btn-secondary',
                style: { padding: '6px 10px', fontSize: '12px' }
              }, t('common.edit')),
              React.createElement('button', {
                onClick: function() { handleDelete(o.id); },
                className: 'btn btn-secondary',
                style: { padding: '6px 10px', fontSize: '12px', color: 'var(--error)' }
              }, t('common.delete'))
            )
          );
        })
  );
}

export default AdminOffers;
